
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserDirectorboards from 'Models/Security/Acl/UserDirectorboards';
import VisitorsDirectorboards from 'Models/Security/Acl/VisitorsDirectorboards';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IDirectorboardsEntityAttributes extends IModelAttributes {
	iscurrent: boolean;

	directorId: string;
	director: Models.DirectorEntity | Models.IDirectorEntityAttributes;
	boardId: string;
	board: Models.BoardEntity | Models.IBoardEntityAttributes;

}

@entity('DirectorboardsEntity', 'DirectorBoards')
export default class DirectorboardsEntity extends Model
	implements IDirectorboardsEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserDirectorboards(),
		new VisitorsDirectorboards(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<DirectorboardsEntity, boolean>()
	@CRUD({
		name: 'IsCurrent',
		displayType: 'checkbox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public iscurrent: boolean;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Director',
		displayType: 'reference-combobox',
		order: 20,
		referenceTypeFunc: () => Models.DirectorEntity,
		
	})
	public directorId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public director: Models.DirectorEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Board',
		displayType: 'reference-combobox',
		order: 30,
		referenceTypeFunc: () => Models.BoardEntity,
		
	})
	public boardId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public board: Models.BoardEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IDirectorboardsEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IDirectorboardsEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.iscurrent !== undefined) {
				this.iscurrent = attributes.iscurrent;
			}
			if (attributes.directorId !== undefined) {
				this.directorId = attributes.directorId;
			}
			if (attributes.director !== undefined) {
				if (attributes.director === null) {
					this.director = attributes.director;
				} else if (attributes.director instanceof Models.DirectorEntity) {
					this.director = attributes.director;
					this.directorId = attributes.director.id;
				} else {
					this.director = new Models.DirectorEntity(attributes.director);
					this.directorId = this.director.id;
				}
			}
			if (attributes.boardId !== undefined) {
				this.boardId = attributes.boardId;
			}
			if (attributes.board !== undefined) {
				if (attributes.board === null) {
					this.board = attributes.board;
				} else if (attributes.board instanceof Models.BoardEntity) {
					this.board = attributes.board;
					this.boardId = attributes.board.id;
				} else {
					this.board = new Models.BoardEntity(attributes.board);
					this.boardId = this.board.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		director {
			${Models.DirectorEntity.getAttributes().join('\n')}
		}
		board {
			${Models.BoardEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(DirectorboardsEntity.prototype, 'created');
CRUD(modifiedAttr)(DirectorboardsEntity.prototype, 'modified');
